"use client";

import React, { useMemo, useCallback } from "react";
import { useContactsPaginated } from "@/features/contacts/hooks";
import { useContactsByDomain, extractDomainFromEmail } from "@/features/contacts/hooks/contacts-by-domain";
import { Building2, Plus, Upload, Search, Globe, Building } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useGlobalModalStack, MODAL_IDS } from "@west-tennessee-consulting/ui";
import {
  EnhancedTabs,
  EnhancedTabsList,
  EnhancedTabsTrigger,
  EnhancedTabsContent,
} from "@/components/ui/tabs/enhanced-tabs";
import ContactCsvImportModal from "../ContactCsvImportModal";
import { QuickContactCreationModal } from "../QuickContactCreationModal";
import { DomainSearchModal } from "../DomainSearchModal";
import { CompanyNameContacts } from "./CompanyNameContacts";
import { DomainContacts } from "./DomainContacts";
import type { CompanyContactsProps } from "./types";

export const CompanyContacts: React.FC<CompanyContactsProps> = ({
  companyName,
  currentContactId,
  currentContact,
  className = "",
}) => {
  const { openModal } = useGlobalModalStack();
  
  // Handler to open domain search modal
  const handleOpenDomainSearch = useCallback(() => {
    if (currentContact) {
      openModal(MODAL_IDS.navigation.domainSearch, {
        existingContact: currentContact,
        onContactImported: () => {
          void refetchCompany?.();
        },
      });
    }
  }, [currentContact, openModal]);
  
  // Extract domain from current contact's email
  const emailDomain = useMemo(() => {
    return currentContact?.email ? extractDomainFromEmail(currentContact.email) : null;
  }, [currentContact?.email]);

  // Fetch colleagues by company name using existing hook with RLS
  const {
    data: colleaguesData,
    isLoading: isLoadingCompany,
    error: companyError,
    refetch: refetchCompany,
  } = useContactsPaginated({
    company_name: companyName || undefined,
    exclude_id: currentContactId,
    limit: 10, // Show up to 10 colleagues
    isActive: true, // Only active contacts
  });

  // Fetch colleagues by email domain
  const {
    contacts: domainContacts,
    isLoading: isLoadingDomain,
    error: domainError,
    refetch: refetchDomain,
    totalContacts: totalDomainContacts,
  } = useContactsByDomain(emailDomain, currentContactId);

  const colleagues = colleaguesData?.data || [];
  const totalColleagues = colleaguesData?.totalCount || 0;

  // Combined refetch function
  const refetch = () => {
    void refetchCompany?.();
    void refetchDomain?.();
  };

  // Don't render if no company name and no email domain
  if (!companyName && !emailDomain) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <div className="flex items-center gap-3 text-muted-foreground">
            <Building2 className="h-5 w-5" />
            <span>No company or email domain associated with this contact</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (companyError || domainError) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <div className="text-red-600">Error loading company contacts</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Building2 className="h-5 w-5 text-blue-600" />
            <div>
              <div className="text-lg font-semibold">Company Contacts</div>
              <div className="text-sm text-muted-foreground font-normal">
                {companyName && emailDomain
                  ? `Contacts at ${companyName} and ${emailDomain} domain`
                  : companyName
                    ? `Contacts at ${companyName}`
                    : emailDomain
                      ? `Contacts at ${emailDomain} domain`
                      : "Related contacts"}
              </div>
            </div>
          </div>

          {/* Add Contact Actions - only show if we have current contact data */}
          {currentContact && (
            <div className="flex items-center gap-2">
              {/* Domain Search */}
              <Button
                variant="outline"
                size="sm"
                className="gap-2 bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200 hover:border-blue-400 hover:bg-gradient-to-r hover:from-blue-100 hover:to-blue-200 text-blue-700 hover:text-blue-800 font-medium transition-all duration-200 shadow-sm hover:shadow-md"
                onClick={handleOpenDomainSearch}
              >
                <Search className="h-4 w-4" />
                Search Domain
              </Button>

              {/* Quick Add Contact */}
              <QuickContactCreationModal
                existingContact={currentContact}
                onContactCreated={() => {
                  // Refresh the colleagues list
                  void refetchCompany?.();
                }}
                trigger={
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2 bg-gradient-to-r from-green-50 to-green-100 border-green-200 hover:border-green-400 hover:bg-gradient-to-r hover:from-green-100 hover:to-green-200 text-green-700 hover:text-green-800 font-medium transition-all duration-200 shadow-sm hover:shadow-md"
                  >
                    <Plus className="h-4 w-4" />
                    Add Contact
                  </Button>
                }
              />

              {/* CSV Import */}
              <ContactCsvImportModal
                existingContact={currentContact}
                onSuccess={() => {
                  // Refresh the colleagues list
                  void refetchCompany?.();
                }}
                triggerButton={
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2 bg-gradient-to-r from-purple-50 to-purple-100 border-purple-200 hover:border-purple-400 hover:bg-gradient-to-r hover:from-purple-100 hover:to-purple-200 text-purple-700 hover:text-purple-800 font-medium transition-all duration-200 shadow-sm hover:shadow-md"
                  >
                    <Upload className="h-4 w-4" />
                    Import CSV
                  </Button>
                }
              />
            </div>
          )}
        </CardTitle>
      </CardHeader>

      <CardContent className="p-6 pt-0">
        <EnhancedTabs defaultValue={companyName ? "company" : "domain"} className="w-full">
          <EnhancedTabsList className="grid w-full grid-cols-2">
            {companyName && (
              <EnhancedTabsTrigger
                value="company"
                variant="blue"
                className="flex items-center gap-2"
              >
                <Building className="h-4 w-4" />
                Company Name
                {totalColleagues > 0 && (
                  <Badge variant="secondary" className="ml-1">
                    {totalColleagues}
                  </Badge>
                )}
              </EnhancedTabsTrigger>
            )}
            {emailDomain && (
              <EnhancedTabsTrigger
                value="domain"
                variant="green"
                className="flex items-center gap-2"
              >
                <Globe className="h-4 w-4" />
                Email Domain
                {totalDomainContacts > 0 && (
                  <Badge variant="secondary" className="ml-1">
                    {totalDomainContacts}
                  </Badge>
                )}
              </EnhancedTabsTrigger>
            )}
          </EnhancedTabsList>

          {companyName && (
            <EnhancedTabsContent value="company" className="mt-4">
              <CompanyNameContacts
                colleagues={colleagues}
                totalColleagues={totalColleagues}
                companyName={companyName}
                isLoading={isLoadingCompany}
              />
            </EnhancedTabsContent>
          )}

          {emailDomain && (
            <EnhancedTabsContent value="domain" className="mt-4">
              <DomainContacts
                contacts={domainContacts}
                totalContacts={totalDomainContacts}
                domain={emailDomain}
                isLoading={isLoadingDomain}
              />
            </EnhancedTabsContent>
          )}
        </EnhancedTabs>
      </CardContent>
    </Card>
  );
};
